import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
// Menu API moved to direct API calls
import { apiClient } from "../../base_url/config"

interface MenuParams {
  branchId?: number
  typeId?: number
  language?: string
  forceRefresh?: boolean
}

// Updated interfaces to support multilingual data
interface MultilingualText {
  ar?: string
  en?: string
}

interface Product {
  id: number
  name: string | MultilingualText
  name_ar?: string
  name_en?: string
  description: string | MultilingualText
  description_ar?: string
  description_en?: string
  image: string
  price: string
}

interface Category {
  id: number
  name: string | MultilingualText
  name_ar?: string
  name_en?: string
  image: string
  products: Product[]
}

interface MenuState {
  categories: Category[]
  selectedCategory: Category | null
  isLoading: boolean
  error: string | null
  isUsingFallback: boolean
  currentLanguage: string
}

const initialState: MenuState = {
  categories: [],
  selectedCategory: null,
  isLoading: false,
  error: null,
  isUsingFallback: false,
  currentLanguage: localStorage.getItem('language') || localStorage.getItem('i18nextLng') || 'ar',
}

// Async thunk for fetching menu data with language support
export const fetchMenuData = createAsyncThunk(
  "menu/fetchMenuData",
  async (params: MenuParams = {}, { getState, rejectWithValue }) => {
    try {
      const { branchId, typeId, language, forceRefresh = false } = params
      
      // Validate required parameters
      if (!branchId || !typeId) {
        return rejectWithValue("branchId and typeId are required parameters")
      }
      
      // Get current language from localStorage if not provided
      const currentLanguage = language || 
                             localStorage.getItem('language') || 
                             localStorage.getItem('i18nextLng') || 
                             'ar'
      
      
      const response = await apiClient.get(`/qr/menus/branches/${branchId}`, {
        params: { 
          typeId,
          // Language will be automatically added by interceptor
        }
      })
      
      const menuData = response.data.data
      
      return {
        categories: menuData.categories,
        language: currentLanguage,
        isUsingFallback: false,
        skipUpdate: false,
      }
    } catch (error: any) {
      
      // Handle different error scenarios with specific messages
      if (error.response?.status === 404) {
        return rejectWithValue(`الفرع غير موجود (Branch ID: ${params.branchId})`)
      }
      
      if (error.response?.status === 500) {
        return rejectWithValue(`خطأ في الخادم - يرجى المحاولة مرة أخرى`)
      }
      
      if (error.response?.status === 400) {
        return rejectWithValue(`معاملات غير صحيحة - يرجى التحقق من الرابط`)
      }
      
      if (error.response?.status === 401) {
        return rejectWithValue(`غير مصرح بالوصول - يرجى التحقق من الصلاحيات`)
      }
      
      // Handle network errors
      if (error.code === 'NETWORK_ERROR' || !error.response) {
        return rejectWithValue(`خطأ في الاتصال - يرجى التحقق من الإنترنت`)
      }
      
      // Generic error message
      return rejectWithValue(error.response?.data?.message || error.message || "فشل في تحميل القائمة")
    }
  },
)

const menuSlice = createSlice({
  name: "menu",
  initialState,
  reducers: {
    setSelectedCategory: (state, action: PayloadAction<Category>) => {
      state.selectedCategory = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenuData.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchMenuData.fulfilled, (state, action) => {
        state.isLoading = false
        
        // Only update state if this is not a skip (concurrent call)
        if (!action.payload.skipUpdate) {
          state.categories = action.payload.categories
          state.isUsingFallback = action.payload.isUsingFallback
          state.currentLanguage = action.payload.language
          
          // Set first category as selected by default
          if (action.payload.categories.length > 0) {
            state.selectedCategory = action.payload.categories[0]
          }
          // Clear any previous errors
          state.error = null
          
        }
      })
      .addCase(fetchMenuData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string || action.error.message || "Failed to fetch menu"
        state.isUsingFallback = false
      })
  },
})

// Utility functions for handling multilingual text
export const getLocalizedText = (
  textObject: string | MultilingualText | undefined,
  language: string,
  fallbackLanguage: string = 'ar'
): string => {
  if (!textObject) return ''
  
  // If it's already a string, return it
  if (typeof textObject === 'string') return textObject
  
  // If it's an object, extract the appropriate language
  const langText = textObject[language as keyof MultilingualText]
  if (langText && langText.trim()) return langText
  
  // Fallback to other language
  const fallbackText = textObject[fallbackLanguage as keyof MultilingualText]
  if (fallbackText && fallbackText.trim()) return fallbackText
  
  // If all else fails, return first available text
  return textObject.ar || textObject.en || ''
}

// Extract localized text from API response that might have name_ar/name_en format
export const getLocalizedTextFromApiResponse = (
  item: any,
  field: string,
  language: string,
  fallbackLanguage: string = 'ar'
): string => {
  // First try the object format (name: {ar: "...", en: "..."})
  if (item[field] && typeof item[field] === 'object') {
    return getLocalizedText(item[field], language, fallbackLanguage)
  }
  
  // Then try the separate fields format (name_ar, name_en)
  const langField = `${field}_${language}`
  const fallbackField = `${field}_${fallbackLanguage}`
  
  if (item[langField] && item[langField].trim()) return item[langField]
  if (item[fallbackField] && item[fallbackField].trim()) return item[fallbackField]
  
  // Finally fallback to the main field
  return item[field] || ''
}

export const { setSelectedCategory, clearError } = menuSlice.actions
export default menuSlice.reducer
